import {
  Drawer,
  DrawerContent,
  DrawerPortal,
  DrawerOverlay,
} from "@/components/ui/drawer";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Trophy, ArrowLeft, Loader2, Pencil, Clock } from "lucide-react";

interface MatchHistoryData {
  id: string;
  type: 'league' | 'global';
  leagueId?: string;
  leagueName?: string;
  playedAt: Date | string;
  status: string;
  winner: number;
  score: any;
  userTeam: number;
  userWon: boolean;
  ratingBefore: number;
  ratingAfter: number;
  ratingDelta: number;
  levelBefore?: number;
  levelAfter?: number;
  levelDelta?: number;
  opponents: string[];
  teammate?: string;
  expectedProbability?: number;
  playerImage1?: string;
  playerImage2?: string;
  playerImage3?: string;
  playerImage4?: string;
  team1Player1Image?: string | null;
  team1Player2Image?: string | null;
  team2Player1Image?: string | null;
  team2Player2Image?: string | null;
  team1Player1Name?: string | null;
  team1Player2Name?: string | null;
  team2Player1Name?: string | null;
  team2Player2Name?: string | null;
  reportedById?: string;
  team1Player1Id?: string;
  team1Player2Id?: string | null;
  team2Player1Id?: string;
  team2Player2Id?: string | null;
  homeClubId?: string | null;
  homeClubName?: string | null;
}

interface MatchHistoryDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  match: MatchHistoryData | null;
  viewerName?: string;
  isViewerParticipant?: boolean;
  currentUserId?: string;
  isLeagueAdmin?: boolean;
  onEdit?: (match: MatchHistoryData) => void;
}

export function MatchHistoryDrawer({ 
  open, 
  onOpenChange, 
  match,
  viewerName = "Jugador",
  isViewerParticipant = true,
  currentUserId,
  isLeagueAdmin = false,
  onEdit
}: MatchHistoryDrawerProps) {
  const isReporter = currentUserId && match?.reportedById === currentUserId;
  const canEdit = match && match.status === "pending" && (isReporter || (isLeagueAdmin && match.type === 'league'));
  
  const getScoreSets = (score: any) => {
    if (typeof score === 'string') {
      return score.split(',').map(s => {
        const [team1, team2] = s.trim().split('-').map(Number);
        return { team1, team2 };
      });
    }
    if (score?.sets && Array.isArray(score.sets)) {
      return score.sets;
    }
    return [];
  };

  const getInitials = (name: string | undefined | null) => {
    if (!name) return '?';
    return name.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase();
  };

  const renderContent = () => {
    if (!match) {
      return (
        <div className="flex items-center justify-center h-full">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      );
    }

    const sets = getScoreSets(match.score);
    const prob = match.userTeam === 1 
      ? (match.expectedProbability || 50) 
      : (100 - (match.expectedProbability || 50));
    const is50_50 = prob === 50;

    // Team 1 is always the left team in the UI
    const team1Score = sets.map((s: any) => s.team1);
    const team2Score = sets.map((s: any) => s.team2);

    let team1TotalGames = 0;
    let team2TotalGames = 0;
    sets.forEach((set: any) => {
      team1TotalGames += set.team1 || 0;
      team2TotalGames += set.team2 || 0;
    });
    
    const totalGames = team1TotalGames + team2TotalGames;
    const team1Percentage = totalGames > 0 ? Math.round((team1TotalGames / totalGames) * 100) : 0;
    const team2Percentage = totalGames > 0 ? Math.round((team2TotalGames / totalGames) * 100) : 0;
    
    const team1SetWins = sets.filter((s: any) => s.team1 > s.team2).length;
    const team2SetWins = sets.filter((s: any) => s.team2 > s.team1).length;
    
    // Determine winner based on database match.winner if available, fallback to logic
    const team1Won = match.winner === 1 || (match.winner === undefined && (team1SetWins > team2SetWins || (team1SetWins === team2SetWins && team1TotalGames > team2TotalGames)));

    const getTeam1Player1Name = () => {
      const name = (match as any).team1Player1Name || (match as any).player1Name;
      if (name && !name.toLowerCase().includes('jugador')) return name;
      return name || 'Jugador 1';
    };

    const getTeam1Player2Name = () => {
      const name = (match as any).team1Player2Name || (match as any).player2Name;
      if (name && !name.toLowerCase().includes('jugador')) return name;
      return name || '';
    };

    const getTeam2Player1Name = () => {
      const name = (match as any).team2Player1Name || (match as any).player3Name;
      if (name && !name.toLowerCase().includes('jugador')) return name;
      const opponentName = match.opponents && match.opponents[0];
      if (opponentName && !opponentName.toLowerCase().includes('jugador')) return opponentName;
      return name || opponentName || 'Jugador 2';
    };

    const getTeam2Player2Name = () => {
      const name = (match as any).team2Player2Name || (match as any).player4Name;
      if (name && !name.toLowerCase().includes('jugador')) return name;
      const opponentName = match.opponents && match.opponents[1];
      if (opponentName && !opponentName.toLowerCase().includes('jugador')) return opponentName;
      return name || opponentName || '';
    };

    return (
      <>
        {/* Date Header */}
        <div className="text-center text-sm text-muted-foreground">
          {new Date(match.playedAt).toLocaleDateString('es-DO', { 
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          })} - {new Date(match.playedAt).toLocaleTimeString('es-DO', {
            hour: '2-digit',
            minute: '2-digit'
          })}
        </div>

        {(match.leagueName || match.homeClubName) ? (
          <div
            className="text-center text-xs text-muted-foreground -mt-3"
            data-testid="text-match-history-context"
          >
            {[match.leagueName, match.homeClubName].filter(Boolean).join(" · ")}
          </div>
        ) : null}

        {/* Teams with Score in Center - 4 Players */}
        <div className="grid grid-cols-3 gap-4 items-start">
          {/* Team 1 - Left Side (2 players) */}
          <div className="flex flex-col gap-3">
            {/* Player 1 */}
            <div className="flex flex-col items-center gap-1">
              <Avatar className="h-14 w-14 border-2 border-primary">
                {(match.team1Player1Image || match.playerImage1) && (
                  <AvatarImage src={match.team1Player1Image || match.playerImage1} alt="Player 1" />
                )}
                <AvatarFallback className="text-xs font-bold">
                  {getInitials(getTeam1Player1Name())}
                </AvatarFallback>
              </Avatar>
              <div className="text-center">
                <p className="text-xs font-bold leading-tight truncate max-w-[15ch]">
                  {getTeam1Player1Name()}
                </p>
              </div>
            </div>

            {/* Player 2 */}
            {getTeam1Player2Name() && (
              <div className="flex flex-col items-center gap-1">
                <Avatar className="h-14 w-14 border-2 border-primary">
                  {(match.team1Player2Image || match.playerImage2) && (
                    <AvatarImage src={match.team1Player2Image || match.playerImage2} alt="Player 2" />
                  )}
                  <AvatarFallback className="text-xs font-bold">
                    {getInitials(getTeam1Player2Name())}
                  </AvatarFallback>
                </Avatar>
                <div className="text-center">
                  <p className="text-xs font-bold leading-tight truncate max-w-[15ch]">
                    {getTeam1Player2Name()}
                  </p>
                </div>
              </div>
            )}

            {/* Points */}
            <div className="text-center pt-2">
              {match.userTeam === 1 && match.ratingDelta > 0 ? (
                <span className="text-sm font-bold text-green-600">+{match.ratingDelta}</span>
              ) : match.userTeam === 1 && match.ratingDelta < 0 ? (
                <span className="text-sm font-bold text-red-600">{match.ratingDelta}</span>
              ) : match.userTeam === 2 && match.ratingDelta > 0 ? (
                <span className="text-sm font-bold text-red-600">-{match.ratingDelta}</span>
              ) : match.userTeam === 2 && match.ratingDelta < 0 ? (
                <span className="text-sm font-bold text-green-600">+{Math.abs(match.ratingDelta)}</span>
              ) : null}
            </div>
          </div>

          {/* Score in Center - Sets Horizontal */}
          <div className="flex flex-col items-center justify-center gap-1 py-2">
            {sets.length > 0 && (
              <div className="space-y-1">
                {sets.map((set: any, idx: number) => {
                  return (
                    <div key={idx} className="text-center leading-tight flex gap-1 items-center justify-center">
                      <div className={`text-lg tabular-nums ${set.team1 > set.team2 ? 'font-bold' : 'font-normal'}`}>{set.team1}</div>
                      <div className="text-xs text-muted-foreground">-</div>
                      <div className={`text-lg tabular-nums ${set.team2 > set.team1 ? 'font-bold' : 'font-normal'}`}>{set.team2}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Team 2 - Right Side (2 players) */}
          <div className="flex flex-col gap-3">
            {/* Player 3 */}
            <div className="flex flex-col items-center gap-1">
              <Avatar className="h-14 w-14 border-2 border-primary">
                {(match.team2Player1Image || match.playerImage3) && (
                  <AvatarImage src={match.team2Player1Image || match.playerImage3} alt="Player 3" />
                )}
                <AvatarFallback className="text-xs font-bold">
                  {getInitials(getTeam2Player1Name())}
                </AvatarFallback>
              </Avatar>
              <div className="text-center">
                <p className="text-xs font-bold leading-tight truncate max-w-[15ch]">
                  {getTeam2Player1Name()}
                </p>
              </div>
            </div>

            {/* Player 4 */}
            {getTeam2Player2Name() && (
              <div className="flex flex-col items-center gap-1">
                <Avatar className="h-14 w-14 border-2 border-primary">
                  {(match.team2Player2Image || match.playerImage4) && (
                    <AvatarImage src={match.team2Player2Image || match.playerImage4} alt="Player 4" />
                  )}
                  <AvatarFallback className="text-xs font-bold">
                    {getInitials(getTeam2Player2Name())}
                  </AvatarFallback>
                </Avatar>
                <div className="text-center">
                  <p className="text-xs font-bold leading-tight truncate max-w-[15ch]">
                    {getTeam2Player2Name()}
                  </p>
                </div>
              </div>
            )}

            {/* Points */}
            <div className="text-center pt-2">
              {match.userTeam === 2 && match.ratingDelta > 0 ? (
                <span className="text-sm font-bold text-green-600">+{match.ratingDelta}</span>
              ) : match.userTeam === 2 && match.ratingDelta < 0 ? (
                <span className="text-sm font-bold text-red-600">{match.ratingDelta}</span>
              ) : match.userTeam === 1 && match.ratingDelta > 0 ? (
                <span className="text-sm font-bold text-red-600">-{match.ratingDelta}</span>
              ) : match.userTeam === 1 && match.ratingDelta < 0 ? (
                <span className="text-sm font-bold text-green-600">+{Math.abs(match.ratingDelta)}</span>
              ) : null}
            </div>
          </div>
        </div>

        {/* Prediction Bar */}
        <div className="space-y-2">
          <p className="text-center text-sm font-medium">Predicción</p>
          <div className="flex gap-2 items-center h-8">
            <div className="flex-1 bg-muted rounded-full h-full flex items-center justify-center">
              <span className="text-xs font-bold text-muted-foreground">{prob}%</span>
            </div>
            <div className="flex-1 bg-muted rounded-full h-full flex items-center justify-center">
              <span className="text-xs font-bold text-muted-foreground">{100 - prob}%</span>
            </div>
          </div>
        </div>

        {/* Result Bar */}
        <div className="space-y-2">
          <p className="text-center text-sm font-medium">Resultado</p>
          <div className="flex gap-2 items-center h-8">
            {team1Won ? (
              <>
                <div className="flex-1 bg-primary rounded-full h-full flex items-center justify-center">
                  <span className="text-xs font-bold text-white">{team1Percentage}%</span>
                </div>
                <div className="flex-1 bg-muted rounded-full h-full flex items-center justify-center">
                  <span className="text-xs font-bold text-muted-foreground">{team2Percentage}%</span>
                </div>
              </>
            ) : (
              <>
                <div className="flex-1 bg-muted rounded-full h-full flex items-center justify-center">
                  <span className="text-xs font-bold text-muted-foreground">{team1Percentage}%</span>
                </div>
                <div className="flex-1 bg-primary rounded-full h-full flex items-center justify-center">
                  <span className="text-xs font-bold text-white">{team2Percentage}%</span>
                </div>
              </>
            )}
          </div>
        </div>
      </>
    );
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerPortal>
        <DrawerOverlay className="z-[200]" />
        <DrawerContent className="h-[90vh] z-[201]">
          <div className="mx-auto w-full max-w-lg h-full flex flex-col pb-8">
            <header className="sticky top-0 z-[210] bg-background/95 backdrop-blur border-b flex-shrink-0">
              <div className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-3">
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => onOpenChange(false)}
                    data-testid="button-back-match-drawer"
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </Button>
                  <h1 className="text-lg font-semibold flex items-center gap-2">
                    <Trophy className="h-5 w-5 text-primary" />
                    Resumen del Partido
                  </h1>
                  {match?.status === "pending" && (
                    <Badge variant="secondary" className="text-xs">
                      <Clock className="h-3 w-3 mr-1" />
                      Pendiente
                    </Badge>
                  )}
                </div>
                {canEdit && onEdit && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onEdit(match)}
                    data-testid="button-edit-match"
                  >
                    <Pencil className="h-4 w-4 mr-1" />
                    Editar
                  </Button>
                )}
              </div>
            </header>
            <div className="flex-1 overflow-y-auto px-4 pt-4 space-y-6">
              {renderContent()}
            </div>
          </div>
        </DrawerContent>
      </DrawerPortal>
    </Drawer>
  );
}